// Material-UI Components
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Checkbox,
  TextField,
  Alert,
  Chip,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';

// Material-UI Icons
import {
  AutoMode as AutomatedIcon,
  Handyman as ManualIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  AccountBalanceWallet as PaymentInfoIcon,
  Refresh as RetryIcon,
} from '@mui/icons-material';

// React and Hooks
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-query';

// Custom Hooks, Components, and Utilities
import useIsDesktop from '../../../../../Hooks/useIsDesktop';
import { dateToDatetimeLocal, withdrawalManualServices, withdrawalStatuses } from '../../../../../Utils/constants';
import { RateField } from '../../../../../Components/RateField';
import AlertDialog from '../../../../../Components/AlertDialog';
import useAlertDialog from '../../../../../Components/useAlertDialog';
import API from '../../../../../API';
import CreatorPaymentDialog from '../../helpers/creatortaxwalletinfo';
import CreatorBlitzPayRPAStatus from '../../helpers/RPAwithdrwal';

// WithdrawalToSubjectData function
const WithdrawalToSubjectData = (wdw) => {
  switch (wdw.entity_type) {
    case 'company':
      return {
        subject: wdw.company.name,
        subjectPaymentMethod: 'N/A',
        subjectPaymentEmail: 'N/A',
        subjectPicture: wdw.company.phphref,
      };
    case 'creator':
      let subjectPaymentEmail = wdw.creator?.payout_email || 'N/A';
      let subjectPaymentMethod = 'paypal';

      // Check withdrawal's actual payment_service first (set during processing)
      if (wdw.payment_service) {
        subjectPaymentMethod = wdw.payment_service;
      } else if (wdw.creator_payment_info?.suggested_payment) {
        // Use creator_payment_info if available (new wallet system)
        subjectPaymentMethod = wdw.creator_payment_info.suggested_payment.method;
        subjectPaymentEmail = wdw.creator_payment_info.suggested_payment.account;
      } else if (wdw.creator?.user) {
        // Fallback to legacy payment method detection
        if (wdw.creator.user.payout_preferred == 'PayPal') {
          subjectPaymentMethod = 'paypal';
          subjectPaymentEmail = wdw.creator.user.paypal || subjectPaymentEmail;
        } else if (wdw.creator.user.payout_preferred == 'Stripe') {
          subjectPaymentMethod = 'stripe';
          subjectPaymentEmail = wdw.creator.user.stripe_id;
        }
      }

      return {
        subject: wdw.creator.creator,
        subjectPaymentMethod,
        subjectPaymentEmail,
        subjectPicture: wdw.creator.pfphref,
        hasModernWallet: wdw.creator_payment_info?.has_modern_setup || false,
        primaryWallet: wdw.creator_payment_info?.primary_wallet || null,
        walletCount: wdw.creator_payment_info?.wallets?.length || 0,
      };
    case 'user':
      return {
        subject: wdw.global_user?.username || wdw.global_user?.email || 'Unknown User',
        subjectPaymentMethod: wdw.payment_service || 'N/A',
        subjectPaymentEmail: wdw.recipient_email || wdw.global_user?.email || 'N/A',
        subjectPicture: null,
        entityType: 'user',
        entityId: wdw.user_id,
      };
    default:
      return {
        subject: 'Unknown',
        subjectPaymentMethod: 'N/A',
        subjectPaymentEmail: 'N/A',
        subjectPicture: null,
        entityType: wdw.entity_type || 'unknown',
        entityId: 'N/A',
      };
  }
};

// WithdrawalToProjectData function
const WithdrawalToProjectData = (wdw) => {
  if (wdw.invoice) {
    return {
      projectType: 'Invoice',
      projectId: wdw.invoice.id,
      projectPoNumber: wdw.invoice.po_number,
    };
  }
  if (wdw.payout) {
    return {
      projectType: 'Payout',
      projectId: wdw.payout.id,
      projectPoNumber: wdw.payout.po_number,
    };
  }
  return null;
};

// AutomatedProcessingStatus component
const AutomatedProcessingStatus = ({ withdrawal }) => {
  if (!withdrawal.processing_mode || withdrawal.processing_mode === 'manual') {
    return null;
  }

  const hasBalanceDeducted = withdrawal.balance_deducted_at;
  const processingAttempts = withdrawal.automated_processing_attempts || 0;
  const lastProcessingAttempt = withdrawal.last_processing_attempt;
  const isProcessing = withdrawal.status === 'processing';

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader title="Automated Processing Status" avatar={<AutomatedIcon color="primary" />} />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip icon={<AutomatedIcon />} label="Automated Mode" color="primary" variant="outlined" />
            </Box>
            {hasBalanceDeducted && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip icon={<SuccessIcon />} label="Balance Deducted" color="success" variant="outlined" />
                <Typography variant="caption">{new Date(hasBalanceDeducted).toLocaleString()}</Typography>
              </Box>
            )}
            {processingAttempts > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={`${processingAttempts} processing attempts`}
                  color={processingAttempts > 3 ? 'error' : 'warning'}
                  variant="outlined"
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {lastProcessingAttempt && (
              <Typography variant="body2" color="text.secondary">
                Last attempt: {new Date(lastProcessingAttempt).toLocaleString()}
              </Typography>
            )}
            {isProcessing && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Currently being processed automatically
              </Alert>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// BalanceValidationDisplay component
const BalanceValidationDisplay = ({ withdrawal }) => {
  if (!withdrawal) return null;

  const [balanceInfo, setBalanceInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchBalanceInfo = async () => {
    if (!withdrawal.entity_type) return;
    const entityId = withdrawal.entity_id || withdrawal.company_id || withdrawal.creator_id || withdrawal.user_id;
    if (!entityId) return;

    setLoading(true);
    try {
      const response = await API.withdrawals.getBalance({
        entity_type: withdrawal.entity_type,
        entity_id: entityId,
      });
      setBalanceInfo(response);
    } catch (error) {
      console.error('Failed to fetch balance info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalanceInfo();
  }, [withdrawal.id]);

  if (!balanceInfo) return null;

  const availableBalance = parseFloat(balanceInfo.available_balance || 0);
  const withdrawalAmount = parseFloat(withdrawal.amount_no_fee || 0);
  const hasSufficientBalance = availableBalance >= withdrawalAmount;
  const deficit = withdrawalAmount - availableBalance;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader title="Balance Validation" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Available Balance</Typography>
            <Typography variant="h6" color="primary">${availableBalance.toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Withdrawal Amount</Typography>
            <Typography variant="h6">${withdrawalAmount.toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={12}>
            {hasSufficientBalance ? (
              <Alert severity="success" icon={<SuccessIcon />}>
                Sufficient balance available for withdrawal
              </Alert>
            ) : (
              <Alert severity="error" icon={<ErrorIcon />}>
                Insufficient balance. Deficit: ${deficit.toFixed(2)}
              </Alert>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Main component
export default function AdminWithdrawalDialog({ 
  withdrawalId = null, 
  isOpen = true, 
  onSaveForm = (payload) => 0, 
  onCloseAction = () => 0, 
  onRetry = null 
}) {
  const isDesktop = useIsDesktop();
  const [withdrawalStatus, setWithdrawalStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [recipientMethod, setRecipientMethod] = useState('');
  const [recipientAccount, setRecipientAccount] = useState('');
  const [daysToPay, setDaysToPay] = useState(1);
  const [processingMode, setProcessingMode] = useState('manual');
  const [showAutomatedProcessing, setShowAutomatedProcessing] = useState(false);
  const [updatePaymentData, setUpdatePaymentData] = useState(false);
  const [skipBalanceValidation, setSkipBalanceValidation] = useState(false);

  // Creator payment dialog state
  const [isCreatorPaymentDialogOpen, setCreatorPaymentDialogOpen] = useState(false);

  // NEW: RPA Status state
  const [rpaStatus, setRpaStatus] = useState(null);
  const [showRPAWarning, setShowRPAWarning] = useState(false);

  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  const { data, error, isLoading } = useQuery({
    queryKey: ['withdrawals', withdrawalId],
    queryFn: () => API.withdrawals.adminGet(withdrawalId),
    refetchOnWindowFocus: false,
    enabled: !!withdrawalId,
  });

  useEffect(() => {
    if (data) {
      setWithdrawalStatus(data.status);
      setDaysToPay(data.days_to_payment);
      setRecipientAccount(data.manual_subject || '');
      setRecipientMethod(data.manual_service || '');
      setNotes(data.notes || '');
      setProcessingMode(data.processing_mode || 'manual');
      setShowAutomatedProcessing(data.processing_mode === 'automated');
      setUpdatePaymentData(data.update_payment_data || false);
      setSkipBalanceValidation(false);

      // Auto-populate payment account from wallet data if available
      if (!data.manual_subject && data.entity_type === 'creator' && data.creator_payment_info?.suggested_payment) {
        setRecipientAccount(data.creator_payment_info.suggested_payment.account);
        setRecipientMethod(data.creator_payment_info.suggested_payment.method);
      } else if (!data.manual_subject && data.entity_type === 'creator' && data.creator) {
        // Fallback to legacy creator data
        const subjectData = WithdrawalToSubjectData(data);
        if (subjectData && subjectData.subjectPaymentEmail !== 'N/A') {
          setRecipientAccount(subjectData.subjectPaymentEmail);
          setRecipientMethod(subjectData.subjectPaymentMethod);
        }
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      openDialog('Error', <>Error fetching Withdrawal:<br />{error?.response?.data?.error || error.message}</>);
    }
  }, [error]);

  const isAutomated = data?.processing_mode === 'automated';
  const isQueuedManualCreatorPayout = data?.processing_mode === 'manual_fallback'
    && data?.external_metadata?.manual_queue_reason === 'claim_payout_automation_disabled';
  const disableEdit = !data || data.status === 'paid' || isAutomated;
  const canProcessAutomated = data?.status === 'approved' && isAutomated;
  const isCreatorWithdrawal = data?.entity_type === 'creator' && data?.creator_id;

  // Creator payment dialog handlers
  const handleCreatorPaymentDialogOpen = () => {
    setCreatorPaymentDialogOpen(true);
  };

  const handleCreatorPaymentDialogClose = () => {
    setCreatorPaymentDialogOpen(false);
  };

  // NEW: RPA status change handler
  const handleRPAStatusChange = (status) => {
    setRpaStatus(status);
    setShowRPAWarning(!status.has_signed);
  };

  const setNum = (event, setter) => {
    if (!Number.isNaN(event.target.valueAsNumber)) setter(event.target.valueAsNumber);
  };

  const HandleSelectStatus = (value) => {
    setWithdrawalStatus(value);
    if (value !== 'paid') {
      setSkipBalanceValidation(false);
    }
  };

  const HandleProcessingModeChange = (mode) => {
    setProcessingMode(mode);
    setShowAutomatedProcessing(mode === 'automated');
  };

  const HandleProcessAutomated = async () => {
    try {
      await API.withdrawals.processAutomatedAdmin(withdrawalId, {});
      openDialog('Success', 'Automated withdrawal processing initiated successfully');
      window.location.reload();
    } catch (error) {
      openDialog('Error', <>Error processing automated withdrawal:<br />{error?.response?.data?.error || error.message}</>);
    }
  };

  const HandleCopyFromAvailable = () => {
    if (subjectData && subjectData.subjectPaymentMethod !== 'N/A' && subjectData.subjectPaymentEmail !== 'N/A') {
      setRecipientMethod(subjectData.subjectPaymentMethod);
      setRecipientAccount(subjectData.subjectPaymentEmail);
      setUpdatePaymentData(true);
      openDialog('Success', 'Payment data copied from recipient profile successfully. Payment data will be updated in the database.');
    } else {
      openDialog('Error', 'No payment data available to copy for this recipient. Please fill the payment details manually.');
    }
  };

  const paidByDate = new Date(data?.created_at?.getTime() || Date.now());
  paidByDate.setDate(paidByDate.getDate() + (daysToPay || 99));
  const paidByDatePassed = paidByDate.getTime() < Date.now() && withdrawalStatus !== 'paid';

  const subjectData = data ? WithdrawalToSubjectData(data) : null;
  const projectData = data ? WithdrawalToProjectData(data) : null;

  const HandleSubmit = (e) => {
    e.preventDefault();

    if (isAutomated) {
      openDialog('Error', 'Cannot manually edit automated withdrawals. Use automated processing endpoints instead.');
      return;
    }

    // NEW: Check RPA status before marking as paid
    if (withdrawalStatus === 'paid' && isCreatorWithdrawal && rpaStatus && !rpaStatus.has_signed) {
      openDialog(
        'Warning',
        <>
          <Typography variant="body1" gutterBottom>
            <strong>⚠️ BlitzPay RPA Not Signed</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            This creator has not signed the BlitzPay Revenue Participation Agreement.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Proceeding with payment may result in:</strong>
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <li>Payment processing delays</li>
            <li>Compliance issues</li>
            <li>Additional manual review required</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Are you sure you want to mark this withdrawal as paid without RPA compliance?
          </Typography>
        </>,
        () => {
          closeDialog();
          const payload = {
            status: withdrawalStatus || null,
            notes: notes || null,
            manual_service: recipientMethod || null,
            manual_subject: recipientAccount || null,
            days_to_payment: daysToPay || null,
            processing_mode: processingMode || 'manual',
            update_payment_data: updatePaymentData,
            skip_balance_validation: skipBalanceValidation,
          };
          onSaveForm(payload);
        },
        closeDialog,
        'Proceed Anyway',
        'Cancel'
      );
      return;
    }

    if (withdrawalStatus === 'paid' && (!recipientAccount || !recipientMethod)) {
      openDialog('Error', 'Please fill the transaction data to mark this withdrawal as paid.');
      return;
    }

    const payload = {
      status: withdrawalStatus || null,
      notes: notes || null,
      manual_service: recipientMethod || null,
      manual_subject: recipientAccount || null,
      days_to_payment: daysToPay || null,
      processing_mode: processingMode || 'manual',
      update_payment_data: updatePaymentData,
      skip_balance_validation: skipBalanceValidation,
    };

    onSaveForm(payload);
  };

  return (
    <>
      <Dialog open={isOpen} fullScreen={!isDesktop} maxWidth="false" onClose={onCloseAction} scroll="body">
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <AlertDialog alertState={dialogState}></AlertDialog>
        <form onSubmit={HandleSubmit}>
          <DialogTitle>{`Reference # <${data?.reference || '...'}>`}</DialogTitle>
          <DialogContent dividers>
            <Grid container maxWidth={'60rem'} spacing={2} rowSpacing={2}>
              {/* Automated Processing Status */}
              {data && (
                <Grid item xs={12}>
                  <AutomatedProcessingStatus withdrawal={data} />
                </Grid>
              )}

              {/* Balance Validation */}
              {data && (
                <Grid item xs={12}>
                  <BalanceValidationDisplay withdrawal={data} />
                </Grid>
              )}

              {/* NEW: BlitzPay RPA Status - Only show for creator withdrawals */}
              {isCreatorWithdrawal && data?.creator_id && (
                <Grid item xs={12}>
                  <CreatorBlitzPayRPAStatus
                    creatorUsername={data.creator?.creator}
                    creatorName={data?.creator?.creator || subjectData?.subject}
                    onStatusChange={handleRPAStatusChange}
                    showAssignButton={true}
                  />
                </Grid>
              )}

              {/* Automated Withdrawal Warning */}
              {isAutomated && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <Typography variant="body2">
                      <strong>Automated Withdrawal:</strong> This withdrawal is in automated mode and cannot be manually edited. Use the automated processing endpoints for any changes.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {isQueuedManualCreatorPayout && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2">
                      <strong>Queued Manual Creator Payout:</strong> This withdrawal was created because claim payout automation is disabled. Marking it as paid will not deduct creator balance again.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {/* Processing Mode Selection */}
              {data?.status === 'pending' && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardHeader title="Processing Mode Selection" />
                    <CardContent>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            fullWidth
                            label="Processing Mode"
                            value={processingMode}
                            onChange={(e) => HandleProcessingModeChange(e.target.value)}
                            disabled={disableEdit}
                          >
                            <MenuItem value="manual">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ManualIcon />
                                Manual Processing
                              </Box>
                            </MenuItem>
                            <MenuItem value="automated">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AutomatedIcon />
                                Automated Processing
                              </Box>
                            </MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            {processingMode === 'automated'
                              ? 'Automated processing will handle balance deduction and payment processing automatically.'
                              : 'Manual processing requires manual intervention for each step.'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Automated Processing Button */}
              {canProcessAutomated && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardHeader title="Automated Processing" />
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AutomatedIcon />}
                          onClick={HandleProcessAutomated}
                          disabled={isLoading}
                        >
                          Process Automated Withdrawal
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          This will automatically deduct the balance and initiate payment processing.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid item xs={12} md={12}>
                <TextField fullWidth label="ID" size="small" disabled name="readonly-id" value={data?.id || '...'} />
              </Grid>
              <Grid item xs={12} md={12}>
                <Divider></Divider>
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField fullWidth required label="Brief" name="withdrawal-brief" value={data?.brief || '...'} />
              </Grid>
              <Grid item xs={12} md={6}>
                <RateField
                  fullWidth
                  minValue={0}
                  label="Subtotal"
                  name="withdrawal-amount"
                  value={data?.amount_no_fee.toFixed(2) || '...'}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fee percentage"
                  name="withdrawal-fee"
                  value={data?.fee_percentage.toFixed() || '...'}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <RateField
                  fullWidth
                  minValue={0}
                  label="Amount to Pay"
                  name="withdrawal-amount"
                  value={data ? (data.amount_no_fee * (1 - data.fee_percentage / 100)).toFixed(2) : '...'}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Issued at"
                  name="withdrawal-issued-at"
                  type="datetime-local"
                  value={dateToDatetimeLocal(data?.created_at)}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  error={paidByDatePassed}
                  fullWidth
                  label={data?.payed_at ? 'Paid at' : 'Pay by'}
                  name="withdrawal-issued-at"
                  type="datetime-local"
                  value={dateToDatetimeLocal(data?.payed_at || paidByDate)}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  value={daysToPay}
                  onChange={(event) => setNum(event, setDaysToPay)}
                  variant="outlined"
                  label="Days to Payment"
                  type="number"
                  InputProps={{ inputProps: { min: 1 } }}
                  disabled={disableEdit}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="withdrawal-notes"
                  type="text"
                  multiline
                  minRows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={disableEdit}
                />
              </Grid>
              {withdrawalStatus === 'paid' && !isAutomated ? (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      SelectProps={{ sx: { textTransform: 'capitalize' } }}
                      value={withdrawalStatus}
                      onChange={(e) => HandleSelectStatus(e.target.value)}
                      label="Status"
                      name="withdrawal-status"
                      disabled={disableEdit}
                    >
                      {withdrawalStatuses.map((status) => (
                        <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                          {status}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={skipBalanceValidation}
                          onChange={(e) => setSkipBalanceValidation(e.target.checked)}
                          disabled={disableEdit}
                        />
                      }
                      label="Skip balance check (manual payout)"
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    SelectProps={{ sx: { textTransform: 'capitalize' } }}
                    value={withdrawalStatus}
                    onChange={(e) => HandleSelectStatus(e.target.value)}
                    label="Status"
                    name="withdrawal-status"
                    disabled={disableEdit}
                  >
                    {withdrawalStatuses.map((status) => (
                      <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                        {status}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              {withdrawalStatus === 'paid' && skipBalanceValidation && (
                <Grid item xs={12}>
                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2">
                      Balance validation and internal balance deduction will be bypassed. Use this only when payout is handled externally.
                    </Typography>
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader
                    subheader="Recipient data on file"
                    action={
                      isCreatorWithdrawal && (
                        <Tooltip title="View Creator Payment Information">
                          <IconButton onClick={handleCreatorPaymentDialogOpen} color="primary">
                            <PaymentInfoIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  />
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                      <TextField
                        fullWidth
                        label={`Recipient (${data?.entity_type})`}
                        name="recipient-name"
                        value={subjectData?.subject || '...'}
                      />
                      <Avatar src={subjectData?.subjectPicture} alt={subjectData?.subject}></Avatar>
                      {isCreatorWithdrawal && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PaymentInfoIcon />}
                          onClick={handleCreatorPaymentDialogOpen}
                          sx={{ ml: 1, whiteSpace: 'nowrap' }}
                        >
                          Payment Info
                        </Button>
                      )}
                    </Box>
                    <TextField
                      inputProps={{ sx: { textTransform: 'capitalize' } }}
                      fullWidth
                      label="Preferred payment method"
                      name="recipient-method"
                      value={subjectData?.subjectPaymentMethod || '...'}
                    />
                    <TextField
                      fullWidth
                      label="Payment account on file"
                      name="recipient-account"
                      value={subjectData?.subjectPaymentEmail || '...'}
                    />
                    {isCreatorWithdrawal && subjectData?.hasModernWallet && (
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={`Modern Wallet System: ${subjectData.walletCount} wallet${subjectData.walletCount !== 1 ? 's' : ''}`}
                          color="success"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                        {subjectData.primaryWallet && (
                          <Chip
                            label={`Primary: ${subjectData.primaryWallet.nickname || subjectData.primaryWallet.wallet_type}`}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    )}
                    {isCreatorWithdrawal && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          Click the payment info button above to view detailed wallet and tax information for this creator.
                          {subjectData?.hasModernWallet
                            ? ' This creator is using the modern wallet system.'
                            : ' This creator is using legacy payment methods.'}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader subheader="Transaction data" />
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                      sx={{ alignSelf: 'flex-end' }}
                      onClick={HandleCopyFromAvailable}
                      disabled={disableEdit}
                      variant="outlined"
                      color="primary"
                      startIcon={<InfoIcon />}
                    >
                      Copy from available
                    </Button>
                    <TextField
                      select
                      fullWidth
                      SelectProps={{ sx: { textTransform: 'capitalize' } }}
                      value={recipientMethod}
                      onChange={(e) => setRecipientMethod(e.target.value)}
                      disabled={disableEdit}
                      required={withdrawalStatus === 'paid'}
                    >
                      {withdrawalManualServices.map((service) => (
                        <MenuItem key={service} value={service} sx={{ textTransform: 'capitalize' }}>
                          {service}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      label="Payment account"
                      name="recipient-account"
                      value={recipientAccount}
                      onChange={(e) => setRecipientAccount(e.target.value)}
                      disabled={disableEdit}
                      required={withdrawalStatus === 'paid'}
                      helperText="This payment account information will be automatically saved to the recipient's profile"
                    />
                    <FormControlLabel
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">Update payment data in database?</Typography>
                          <Tooltip
                            title="When enabled, this will update the recipient's payment information in their profile with the transaction data entered above. Payment account data is always persisted regardless of this setting."
                          >
                            <InfoIcon fontSize="small" color="action" />
                          </Tooltip>
                        </Box>
                      }
                      control={
                        <Switch
                          disabled={disableEdit}
                          checked={updatePaymentData}
                          onChange={(e) => setUpdatePaymentData(e.target.checked)}
                        />
                      }
                    />
                    {updatePaymentData && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Enhanced Update Enabled:</strong> This will update additional payment-related fields in the recipient's profile, including payment notes and preferences.
                          {subjectData?.hasModernWallet && ' This will also update the modern wallet system.'}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {projectData && (
                <Grid item xs={12} md={12}>
                  <Card variant="outlined">
                    <CardHeader subheader={`Related Project data (${projectData?.projectType || '...'})`} />
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <TextField
                        inputProps={{ sx: { textTransform: 'capitalize' } }}
                        fullWidth
                        label="Project ID"
                        name="project-id"
                        value={projectData?.projectId || '...'}
                      />
                      <TextField
                        inputProps={{ sx: { textTransform: 'capitalize' } }}
                        fullWidth
                        label="Project PO Number"
                        name="project-ponumber"
                        value={projectData?.projectPoNumber || '...'}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* NEW: Optional comprehensive RPA warning at bottom for non-compliant approved withdrawals */}
              {isCreatorWithdrawal && showRPAWarning && 
               ['approved', 'processing'].includes(withdrawalStatus) && (
                <Grid item xs={12}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <Typography variant="body2">
                      <strong>⚠️ Compliance Alert:</strong> The creator has not signed the BlitzPay RPA. 
                      Processing this withdrawal without RPA compliance may result in payment delays or require additional manual review. 
                      Please ensure RPA is signed before marking as paid.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            {/* Retry button for failed automated withdrawals */}
            {data?.status === 'failed' && data?.processing_mode === 'automated' && onRetry && (
              <Button 
                variant="contained" 
                color="warning" 
                startIcon={<RetryIcon />}
                onClick={() => {
                  onCloseAction();
                  onRetry(data);
                }}
                sx={{ mr: 'auto' }}
              >
                Retry Withdrawal
              </Button>
            )}
            
            <Button variant="text" color="secondary" type="submit" disabled={disableEdit}>
              {isAutomated ? 'Automated (Read-only)' : 'Save'}
            </Button>
            <Button variant="text" color="error" onClick={onCloseAction}>
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Creator Payment Dialog */}
      {isCreatorPaymentDialogOpen && isCreatorWithdrawal && (
        <CreatorPaymentDialog
          open={isCreatorPaymentDialogOpen}
          onClose={handleCreatorPaymentDialogClose}
          creatorId={data?.creator_id}
          creatorName={data?.creator?.creator || subjectData?.subject}
          walletInfo={data?.creator_payment_info}
        />
      )}
    </>
  );
}
